import {
  Button,
  Divider,
  Flex,
  MagnifyingGlassIcon,
  Panel,
  PlusIcon,
  ProgressCircle,
  Text,
  TextField,
} from 'components'
import { useCurrentUser } from 'components/providers'
import { useRef, useState } from 'react'
import { generatePath, useNavigate } from 'react-router'
import { ITrait } from 'types'
import {
  FetchStatus,
  createDoc,
  useBreakpoint,
  useHotKey,
  usePrefCurrentWorkspace,
  useScrolling,
  useTheme,
} from 'utils'
import { RoutePath } from '../../utils/routing'
import { makeEmptyTrait } from '../../utils/traitUtils'
import ContentTile from '../ContentTile'

interface Props {
  status: FetchStatus
  traits: ITrait[]
}

export default function TraitContentList({ status, traits }: Props) {
  const [workspaceId] = usePrefCurrentWorkspace()
  const { scrolling, contentRef, overflowRef } = useScrolling()
  const navigate = useNavigate()
  const user = useCurrentUser()
  const { theme } = useTheme()
  const [search, setSearch] = useState('')
  const isMobile = useBreakpoint() < 1

  const searchRef = useRef<HTMLInputElement>(null)
  useHotKey({ key: 'f', options: { ctrlKey: true } }, () => searchRef.current?.focus())

  function performSearch(val: string): void {
    setSearch(val)
  }

  async function createNewTrait() {
    const { id } = await createDoc(user!.id, 'traits2', makeEmptyTrait(workspaceId!))
    navigate(generatePath(RoutePath.TraitInfo, { workspaceId: workspaceId!, id }))
  }

  if (status === 'loading')
    return (
      <Flex p={20} justifyContent="center" maxW={'100%'}>
        <ProgressCircle isIndeterminate />
      </Flex>
    )

  if (!traits)
    return (
      <Flex p={20} justifyContent="center" maxW={'100%'}>
        <Text>No traits available</Text>
      </Flex>
    )

  return (
    <Flex direction="column" flex={1} overflow="hidden">
      <Flex direction="row" justify="space-between" px={1} pr={isMobile ? 0 : 5}>
        <TextField
          backdropFilter={'blur(14px)'}
          leftElement={
            <MagnifyingGlassIcon color={theme === 'light' ? 'rgba(23, 23, 23, 0.6)' : 'white'} />
          }
          width={{ base: '200px', sm: '300px' }}
          bg={theme === 'light' ? 'rgba(255, 255, 255, 0.74)' : 'rgba(255, 255, 255, 0.14)'}
          borderWidth={0}
          borderRadius="sm"
          boxShadow={'0px 1px 4px 0px rgba(0, 0, 0, 0.06)'}
          disabled={traits.length === 0}
          onChange={e => performSearch(e.target.value)}
          onFocus={e => e.target.select()}
          pl={10}
          placeholder="Search..."
          ref={searchRef}
          value={search}
        />
        <Panel>
          <Button leftIcon={<PlusIcon />} variant="ghost" onClick={() => createNewTrait()}>
            Add Trait
          </Button>
        </Panel>
      </Flex>

      {scrolling && <Divider borderColor="gray.400" mr={5} mt={3} w="auto" />}

      <Flex overflow="auto" ref={overflowRef} direction="column" gap={2} p={4} pl={0}>
        <Flex ref={contentRef} direction="column" gap={2}>
          {traits
            .filter(item => item.title?.en?.toLowerCase().includes(search.toLowerCase()))
            .map((item, index) => (
              <ContentTile
                key={index}
                onEdit={() =>
                  navigate(
                    generatePath(RoutePath.TraitInfo, {
                      workspaceId: workspaceId!,
                      id: item.id,
                    })
                  )
                }
                title={item.title?.en || ''}
              />
            ))}
        </Flex>
      </Flex>
    </Flex>
  )
}
